import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../services/api';

interface MeetingFeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    sessionId: number;
    userId: number;
    onSuccess?: () => void;
}

export const MeetingFeedbackModal: React.FC<MeetingFeedbackModalProps> = ({ isOpen, onClose, sessionId, userId, onSuccess }) => {
    // ... (rest of state)
    const [ratings, setRatings] = useState({
        together: 5,
        reflections: 5,
        topic: 5,
        value: 5
    });

    const [proposals, setProposals] = useState<Array<{ type: 'start' | 'stop' | 'continue'; content: string }>>([]);
    const [newProposal, setNewProposal] = useState({ type: 'start', content: '' });

    if (!isOpen) return null;

    const handleAddProposal = () => {
        if (newProposal.content.trim()) {
            setProposals([...proposals, { type: newProposal.type as any, content: newProposal.content }]);
            setNewProposal({ ...newProposal, content: '' });
        }
    };

    const handleSubmit = async () => {
        try {
            await api.post('/feedback', {
                session_id: sessionId,
                user_id: userId,
                ratings,
                ssc_proposals: proposals
            });
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error("Failed to submit feedback", err);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Meeting Feedback</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    {/* Ratings Section */}
                    <section>
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">How was the meeting? (1-10)</h3>
                        <div className="space-y-6">
                            {[
                                { key: 'together', label: 'Being Together' },
                                { key: 'reflections', label: '5% Reflections' },
                                { key: 'topic', label: 'Deep Dive / Topic' },
                                { key: 'value', label: 'Value for Me' }
                            ].map(({ key, label }) => (
                                <div key={key} className="space-y-2">
                                    <div className="flex justify-between text-sm font-medium text-gray-600">
                                        <span>{label}</span>
                                        <span className="text-blue-600 font-bold">{(ratings as any)[key]}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={(ratings as any)[key]}
                                        onChange={(e) => setRatings({ ...ratings, [key]: parseInt(e.target.value) })}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400">
                                        <span>Struggled</span>
                                        <span>Great</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Start/Stop/Continue Section */}
                    <section>
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Start / Stop / Continue</h3>

                        <div className="flex gap-2 mb-4">
                            <select
                                value={newProposal.type}
                                onChange={(e) => setNewProposal({ ...newProposal, type: e.target.value })}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="start">Start</option>
                                <option value="stop">Stop</option>
                                <option value="continue">Continue</option>
                            </select>
                            <input
                                type="text"
                                value={newProposal.content}
                                onChange={(e) => setNewProposal({ ...newProposal, content: e.target.value })}
                                placeholder="What should we change?"
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddProposal()}
                            />
                            <button
                                onClick={handleAddProposal}
                                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-2">
                            {proposals.map((p, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <span className={`
                      px-2 py-1 text-xs font-bold rounded uppercase
                      ${p.type === 'start' ? 'bg-green-100 text-green-700' :
                                                p.type === 'stop' ? 'bg-red-100 text-red-700' :
                                                    'bg-blue-100 text-blue-700'}
                    `}>
                                            {p.type}
                                        </span>
                                        <span className="text-gray-700">{p.content}</span>
                                    </div>
                                    <button
                                        onClick={() => setProposals(proposals.filter((_, idx) => idx !== i))}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {proposals.length === 0 && (
                                <p className="text-center text-gray-400 text-sm py-4">No proposals added yet.</p>
                            )}
                        </div>
                    </section>
                </div>

                <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-8 py-2 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-transform hover:scale-105 shadow-lg"
                    >
                        Submit Feedback
                    </button>
                </div>
            </div>
        </div>
    );
};
